import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { PanelData } from '@/_shared/lib/charts/types';
import type { SSEMessage } from '@/_shared/types';

import { useEndedStreamHistory } from '../../chat/hooks/useEndedStreamHistory';
import { useSSE } from '../../chat/hooks/useSSE';
import { useTurnStop } from '../../chat/hooks/useTurnStop';
import { buildPanelMentions } from '../../chat/lib/panel-mentions';
import { filterOutTurn, isTurnTerminalInHistory, mergeHistoryWithLiveMessages } from '../../chat/lib/turn-slicing';
import { type PanelDataSummary, summarizePanelData } from '../lib/panel-data-summary';
import { panelError, panelErrorForAssistant } from '../lib/panel-error';
import type { Dashboard } from '../types';

export interface UseDashboardChatReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  sessionId: string | null;
  messages: SSEMessage[];
  connected: boolean;
  /** Demo workspace only: this thread hit its LLM budget and the server will refuse further turns. */
  budgetExhausted: boolean;
  error: string | null;
  /**
   * Matches `MessageInput`'s `onSend` exactly, so this can be passed straight to it.
   *
   * The middle argument is the composer's skill ids, which this surface never uses — but it has to
   * be in the signature. Reading panel ids from slot 2 silently accepted the skill ids instead, and
   * TypeScript allowed it, so the ids never left the browser.
   */
  sendMessage: (content: string, invokedSkillIds?: string[], referencedPanelIds?: string[]) => void;
  resetChat: () => void;
  agentActive: boolean;
  stopping: boolean;
  stopStatusError: boolean;
  stopStalled: boolean;
  retryStopStatus: () => void;
  /** True when session has been created/loaded and messages can be sent. */
  sessionReady: boolean;
  /** Open the sidebar and send a message, queuing if the session isn't ready yet. */
  openAndSend: (message: string, referencedPanelIds?: string[]) => void;
  /** Fire the abort for the active turn. No-op when no turn is active. */
  stopTurn: () => void;
  /** True when the agent is active AND a turn ID has been confirmed — safe to show the Stop button. */
  canStop: boolean;
  /** A message to put back into the composer: one whose send failed. A stopped prompt stays in the transcript. */
  prefillValue: string | undefined;
  /** Bumps each time a new prefill should be applied. */
  prefillNonce: number;
}

export function useDashboardChat(
  dashboard: Dashboard | null,
  paramValues: Record<string, string | string[]>,
): UseDashboardChatReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [adoptedRuntimeTurn, setAdoptedRuntimeTurn] = useState<{ sessionId: string; turnId: string } | null>(null);
  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  const queryClient = useQueryClient();
  const { slug } = useWorkspace();
  const orpc = useOrpc();
  const initializedForSession = useRef<string | null>(null);

  // Reset all state when the dashboard changes (render-phase pattern)
  const dashboardId = dashboard?.id ?? null;
  const [prevDashboardId, setPrevDashboardId] = useState(dashboardId);
  if (dashboardId !== prevDashboardId) {
    setPrevDashboardId(dashboardId);
    setSessionId(null);
    setAgentActive(false);
    setActiveTurnId(null);
    setAdoptedRuntimeTurn(null);
    setPrefillValue(undefined);
    setPrefillNonce(0);
  }

  // Lazy getOrCreate — only fires when sidebar is first opened
  const { data: sessionData, refetch: refetchSession } = useQuery(
    orpc.dashboardChat.getOrCreate.queryOptions({
      input: dashboard && isOpen ? { dashboardId: dashboard.id } : skipToken,
    }),
  );

  // Demo/challenge only: a budget stop ends the THREAD, and `budgetExhausted` is a field of the
  // session fetched once above. Without a refetch on the terminal event the composer stays enabled
  // and the visitor's next send is rejected by the server with a bare FORBIDDEN. Same source and
  // same reasoning as ChatConversation and useAudienceChat.
  const { data: workspaceData } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const demoWorkspace = workspaceData?.challengeMode ?? false;

  // Sync session ID from server data (render-phase pattern)
  // Seeds to null, NOT to serverSessionId: this tracks which server value we've already adopted, and
  // at mount we've adopted none. Seeding it to the current value means a getOrCreate that resolves
  // from the TanStack cache is present on the FIRST render, registers as "already adopted", and never
  // reaches setSessionId — stranding the chat unready until a reload clears the cache (AI-1187).
  const serverSessionId = sessionData?.session?.id ?? null;
  const [prevServerSessionId, setPrevServerSessionId] = useState<string | null>(null);
  if (serverSessionId !== prevServerSessionId) {
    setPrevServerSessionId(serverSessionId);
    if (serverSessionId && serverSessionId !== sessionId) {
      setSessionId(serverSessionId);
    }
  }

  // Stable refs for SSE callbacks to access current state without re-creating the hook
  const dashboardSlugRef = useRef(dashboard?.slug);
  const dashboardIdRef = useRef(dashboard?.id);
  const turnStop = useTurnStop({
    workspaceKey: slug,
    sessionId,
    activeTurnId: activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null,
    stopCall: (input) => orpc.dashboardChat.stopTurn.call(input),
    // A stopped prompt stays in the transcript, marked "Stopped" (the abort marker), with the panels it
    // named. The composer is never touched, so a draft typed during the turn stays put.
    onConfirmed: () => {
      setAgentActive(false);
      setActiveTurnId(null);
    },
  });
  const { noteTerminal, noteStreamEnded, stop, blockingTurnId } = turnStop;
  useEffect(() => {
    dashboardSlugRef.current = dashboard?.slug;
    dashboardIdRef.current = dashboard?.id;
  });

  // Keep the completed transcript and the runtime state in the same cache update. A later remount
  // must not lose a turn whose events arrived through SSE after getOrCreate was last read.
  const cacheTerminalTurn = useCallback(
    (eventType: string, data: Record<string, unknown>, transcript: SSEMessage[]) => {
      const turnId = typeof data['turnId'] === 'string' ? data['turnId'] : null;
      const cachedDashboardId = dashboardIdRef.current;
      if (!turnId || !cachedDashboardId) return;
      const visible = eventType === 'turn_aborted' ? filterOutTurn(transcript, turnId) : transcript;
      const durable = visible.filter((message) => message.seq != null && message.type !== 'turn_aborted');
      queryClient.setQueryData(
        orpc.dashboardChat.getOrCreate.queryOptions({ input: { dashboardId: cachedDashboardId } }).queryKey,
        (prev) => {
          // Optional: a cached session from before `runtime` existed must not throw here. The stream
          // callback swallows that error and then never refreshes the dashboard.
          if (!prev || (prev.runtime?.activeTurn && prev.runtime.activeTurn.turnId !== turnId)) return prev;
          return {
            ...prev,
            sseMessages: mergeHistoryWithLiveMessages(prev.sseMessages, durable),
            runtime: { ...prev.runtime, activeTurn: null },
          };
        },
      );
    },
    [queryClient, orpc],
  );

  // SSE connection with event/disconnect callbacks to avoid set-state-in-effect
  const handleSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>, transcript: SSEMessage[]) => {
      if (eventType === 'turn_complete' || eventType === 'turn_aborted' || eventType === 'error') {
        const id =
          typeof data['turnId'] === 'string'
            ? data['turnId']
            : (activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId);
        if (id) noteTerminal(id);
      }
      if (eventType === 'turn_complete' || eventType === 'error') {
        setAgentActive(false);
        setActiveTurnId(null);
        cacheTerminalTurn(eventType, data, transcript);
        // Only on a demo workspace: everywhere else `budgetExhausted` is never set, so this would be
        // a wasted round-trip after every single turn.
        if (demoWorkspace) void refetchSession();
        // Refresh dashboard + panel data + dynamic param options in case the agent edited it
        const slug = dashboardSlugRef.current;
        if (slug) {
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.query.key(),
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.paramOptions.key(),
          });
        }
      }
      if (eventType === 'turn_aborted') {
        setAgentActive(false);
        setActiveTurnId(null);
        cacheTerminalTurn(eventType, data, transcript);
        if (demoWorkspace) void refetchSession();
        // Invalidate dashboard data — the agent may have saved changes mid-turn
        // before the user clicked stop, so we need to refresh the dashboard view.
        const slug = dashboardSlugRef.current;
        if (slug) {
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.get.queryOptions({ input: { slug } }).queryKey,
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.query.key(),
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.paramOptions.key(),
          });
        }
      }
    },
    [
      queryClient,
      orpc,
      demoWorkspace,
      refetchSession,
      activeTurnId,
      sessionData?.runtime?.activeTurn?.turnId,
      noteTerminal,
      cacheTerminalTurn,
    ],
  );

  // Don't clear agentActive on disconnect — the SSE will retry and reconnect.
  // If the turn completed during the gap, the replayed turn_complete/error
  // event will clear agentActive. Clearing here would falsely unlock the UI
  // while the agent is still running server-side.
  const handleSSEDisconnect = useCallback(() => {
    // intentionally empty — agentActive is managed by SSE events, not connectivity
  }, []);

  const sseOptions = useMemo(
    () => ({
      onEvent: handleSSEEvent,
      onDisconnect: handleSSEDisconnect,
    }),
    [handleSSEEvent, handleSSEDisconnect],
  );

  // SSE connection — turn-scoped: only connect when there's an active turn to follow
  const knownTurnId = activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null;
  const streamUrl =
    sessionId && knownTurnId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${sessionId}/turns/${knownTurnId}/events`
      : null;
  const {
    messages,
    connected,
    error: sseError,
    ended: sseEnded,
    endedTurnId,
    recovered: streamHistoryRecovered,
    recoverHistory,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    clearMessages,
  } = useSSE(streamUrl, {
    ...sseOptions,
    scopeKey: sessionId,
    turnId: knownTurnId,
    recovery: {
      sessionId,
      persistedMessages: sessionData?.sseMessages,
      runtimeTurnId: sessionData?.runtime?.activeTurn?.turnId ?? null,
      refetchSession,
      controlsEnabled: false,
    },
  });
  useEndedStreamHistory({
    sessionId,
    turnId: endedTurnId,
    ended: sseEnded && !streamHistoryRecovered,
    recoverHistory,
  });

  // Restore a turn reported by another server before its first SSE event arrives.
  // Remember the identity after a terminal event so stale query data cannot restore it again.
  const reportedTurnId = sessionData?.runtime?.activeTurn?.turnId ?? null;
  if (
    sessionId &&
    sessionData?.session.id === sessionId &&
    reportedTurnId &&
    (adoptedRuntimeTurn?.sessionId !== sessionId || adoptedRuntimeTurn.turnId !== reportedTurnId) &&
    (!activeTurnId || activeTurnId === reportedTurnId)
  ) {
    setAdoptedRuntimeTurn({ sessionId, turnId: reportedTurnId });
    if (
      !isTurnTerminalInHistory(sessionData.sseMessages, reportedTurnId) &&
      !isTurnTerminalInHistory(messages, reportedTurnId)
    ) {
      setAgentActive(true);
      setActiveTurnId(reportedTurnId);
    }
  }

  useEffect(() => {
    if (sseEnded && knownTurnId) noteStreamEnded(knownTurnId);
  }, [sseEnded, knownTurnId, noteStreamEnded]);

  // The shared Stop hook keeps Send blocked until the database confirms a terminal status.
  // This only clears the local stream marker after a final transport failure.
  useEffect(() => {
    if (agentActive && activeTurnId && sseEnded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async stream failure unlocks the local turn
      setAgentActive(false);
      setActiveTurnId(null);
    }
  }, [agentActive, activeTurnId, sseEnded]);

  // Clear messages when sessionId resets (dashboard switch)
  useEffect(() => {
    if (!sessionId) clearMessages();
  }, [sessionId, clearMessages]);

  // Initialize from historical messages (once per session).
  // Filter out events belonging to the active turn — the SSE stream owns those.
  // Race-safe: if SSE delivered events before sessionData arrived, prepend
  // historical messages instead of replacing.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });

  useEffect(() => {
    if (sessionId && sessionData?.sseMessages && initializedForSession.current !== sessionId) {
      initializedForSession.current = sessionId;
      const filtered = knownTurnId ? filterOutTurn(sessionData.sseMessages, knownTurnId) : sessionData.sseMessages;
      const currentSSE = messagesRef.current;

      if (currentSSE.length > 0) {
        initializeMessages([...filtered, ...currentSSE]);
      } else {
        initializeMessages(filtered);
      }
    }
  }, [sessionId, sessionData?.sseMessages, knownTurnId, initializeMessages]);

  const stopTurn = useCallback(() => {
    const id = knownTurnId ?? blockingTurnId;
    if (id) stop(id);
  }, [knownTurnId, blockingTurnId, stop]);

  // Send message mutation
  const sendMessageMutation = useMutation(orpc.dashboardChat.sendMessage.mutationOptions());

  // Gather dashboard context from React Query cache
  const gatherDashboardContext = useCallback((): {
    title: string;
    description: string | null;
    slug: string;
    warehouseId: string;
    params: Record<string, string | string[]>;
    panels: PanelDataSummary[];
  } | null => {
    if (!dashboard) return null;

    const panelSummaries: PanelDataSummary[] = dashboard.spec.panels.map((panel) => {
      // Read from React Query cache — same query key as PanelWithData uses
      const queryKey = orpc.dashboard.query.queryOptions({
        input: { dashboardId: dashboard.id, panelId: panel.id, params: paramValues },
      }).queryKey;
      const cached = queryClient.getQueryData<PanelData>(queryKey);
      // The Fix button's text: the headline plus the engine's own words, which name the column.
      const failure = panelError(queryClient.getQueryState(queryKey)?.error);

      return summarizePanelData(panel, cached ?? null, failure ? panelErrorForAssistant(failure) : null);
    });

    return {
      title: dashboard.title,
      description: dashboard.description,
      slug: dashboard.slug,
      warehouseId: dashboard.warehouseId,
      params: paramValues,
      panels: panelSummaries,
    };
  }, [dashboard, paramValues, queryClient, orpc.dashboard.query]);

  const sendMessage = useCallback(
    (content: string, _invokedSkillIds?: string[], referencedPanelIds?: string[]) => {
      if (!sessionId || !dashboard || turnStop.blocking) return;
      const ctx = gatherDashboardContext();
      if (!ctx) return;

      // The composer reads the ids from its mention nodes, so nothing is parsed out of `content`.
      // The bubble still needs the labels to mark itself up, so the ids are mapped back through the
      // same rule the composer used — and the message carries its own record from the first paint.
      const mentions = buildPanelMentions(dashboard.spec.panels);
      const referencedPanels = (referencedPanelIds ?? []).flatMap((id) => mentions.filter((m) => m.id === id));

      addMessage({ type: 'user_message', content, ...(referencedPanels.length > 0 && { referencedPanels }) });
      setAgentActive(true);

      const variables = {
        sessionId,
        content,
        dashboardContext: ctx,
        ...(referencedPanelIds?.length && { referencedPanelIds }),
        clientRequestId: crypto.randomUUID(),
      };
      sendMessageMutation.mutate(variables, {
        onSuccess: (data) => {
          if (data.turnId) setActiveTurnId(data.turnId);
        },
        onError: (err: unknown) => {
          removeLastUserMessage(content);
          // TURN_IN_PROGRESS: another turn is already running (post-stop race).
          // Restore the prompt so the user can retry once the abort completes.
          // Reconnect to the active turn's SSE stream.
          const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
          if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
            setPrefillValue(content);
            setPrefillNonce((n) => n + 1);
            setActiveTurnId(errData.activeTurnId);
            return;
          }
          // Surface the error so the user knows the send failed.
          const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
          addMessage({ type: 'error', message: msg });
          setAgentActive(false);
          setActiveTurnId(null);
        },
      });
    },
    [
      sessionId,
      dashboard,
      gatherDashboardContext,
      addMessage,
      removeLastUserMessage,
      sendMessageMutation,
      turnStop.blocking,
    ],
  );

  // Reset mutation
  const resetMutation = useMutation(orpc.dashboardChat.reset.mutationOptions());

  const resetChat = useCallback(() => {
    if (!dashboard) return;
    resetMutation.mutate(
      { dashboardId: dashboard.id },
      {
        onSuccess: () => {
          setSessionId(null);
          clearMessages();
          initializedForSession.current = null;
          setAgentActive(false);
          setActiveTurnId(null);
          void refetchSession();
        },
      },
    );
  }, [dashboard, resetMutation, clearMessages, refetchSession]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Session is ready when getOrCreate has resolved
  const sessionReady = !!sessionId;

  // Queued message for openAndSend — bridges the gap when session isn't ready yet
  const queuedMessage = useRef<{ message: string; referencedPanelIds?: string[] } | null>(null);

  const openAndSend = useCallback(
    (message: string, referencedPanelIds?: string[]) => {
      if (sessionReady && !agentActive && !turnStop.blocking) {
        open();
        sendMessage(message, undefined, referencedPanelIds);
      } else {
        queuedMessage.current = { message, ...(referencedPanelIds && { referencedPanelIds }) };
        open();
      }
    },
    [sessionReady, agentActive, turnStop.blocking, open, sendMessage],
  );

  // Drain queued message once session is ready
  useEffect(() => {
    if (sessionReady && queuedMessage.current && !agentActive && !turnStop.blocking) {
      const queued = queuedMessage.current;
      queuedMessage.current = null;

      sendMessage(queued.message, undefined, queued.referencedPanelIds);
    }
  }, [sessionReady, agentActive, turnStop.blocking, sendMessage]);

  return {
    isOpen,
    open,
    close,
    toggle,
    sessionId,
    messages,
    connected,
    /** Demo workspace only: this thread hit its LLM budget and the server will refuse further turns. */
    budgetExhausted: sessionData?.session?.budgetExhausted ?? false,
    error: streamHistoryRecovered ? null : sseError,
    sendMessage,
    resetChat,
    agentActive: agentActive || turnStop.blocking,
    stopping: turnStop.stopping || turnStop.pending,
    stopStatusError: turnStop.statusError,
    stopStalled: turnStop.stopStalled,
    retryStopStatus: () => {
      void turnStop.retryStatus();
    },
    sessionReady,
    openAndSend,
    stopTurn,
    canStop:
      (agentActive || turnStop.blocking) &&
      (knownTurnId !== null || turnStop.blockingTurnId !== null) &&
      !turnStop.terminalSeen &&
      !turnStop.stopping &&
      !turnStop.pending,
    prefillValue,
    prefillNonce,
  };
}
